import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useTheme } from "@/App";
import { Switch } from "@/components/ui/switch";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import PhoneInputWithCode, { formatPhoneNumber } from "@/components/PhoneInputWithCode";
import {
  Phone, User, Shield, Heart, Flag, Briefcase, Users, Check, ChevronRight, Plus, Trash2, AlertTriangle, X
} from "lucide-react";

// Remove the local formatPhoneNumber since we import it

const PersonalInfoSections = ({ personalInfo, setPersonalInfo, programSelection = {}, onSectionComplete, isSubmitted = false, onBgCheckDeclined, onAgeRestriction }) => {
  const { theme } = useTheme();
  const [activeSubSection, setActiveSubSection] = useState(0);
  const [showBgCheckModal, setShowBgCheckModal] = useState(false);
  const [showAgeVerifyModal, setShowAgeVerifyModal] = useState(false);
  const [pendingDob, setPendingDob] = useState("");
  const [showSsnReminder, setShowSsnReminder] = useState(false);

  const subSections = [
    { id: 0, label: "Contact Information", icon: Phone },
    { id: 1, label: "Judicial Background", icon: Shield },
    { id: 2, label: "Emergency Contact", icon: Heart },
    { id: 3, label: "Citizenship & Identification", icon: Flag },
    { id: 4, label: "US Military Background", icon: Shield },
    { id: 5, label: "Work Experience", icon: Briefcase },
    { id: 6, label: "Demographic Information", icon: Users },
  ];

  const visibleIds = subSections.map(s => s.id);
  const currentIndex = visibleIds.indexOf(activeSubSection);
  const isFirst = currentIndex <= 0;
  const isLast = currentIndex >= visibleIds.length - 1;

  const goNext = () => {
    if (!isLast) {
      if (activeSubSection === 3 && !personalInfo.ssn) {
        setShowSsnReminder(true);
        return;
      }
      setActiveSubSection(visibleIds[currentIndex + 1]);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goPrev = () => {
    if (!isFirst) {
      setActiveSubSection(visibleIds[currentIndex - 1]);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Work Experience is always visible for all programs

  const isSubSectionComplete = (sectionId) => {
    switch (sectionId) {
      case 0:
        return !!(personalInfo.first_name && personalInfo.last_name && personalInfo.email);
      case 1:
        return personalInfo.judicial_agreement != null && personalInfo.judicial_agreement !== "";
      case 2:
        return !!(personalInfo.emergency_first_name && personalInfo.emergency_phone);
      case 3:
        return !!(personalInfo.date_of_birth && personalInfo.gender && personalInfo.sex);
      case 4:
        return personalInfo.veteran_benefits != null && personalInfo.veteran_benefits !== "";
      case 5:
        return personalInfo.years_work_experience != null && personalInfo.years_work_experience !== "";
      case 6:
        return personalInfo.ethnicity != null && personalInfo.ethnicity !== "";
      default:
        return false;
    }
  };

  const renderSubSection = () => {
    switch (activeSubSection) {
      case 0:
        return (
          <div className="space-y-8" data-testid="subsection-contact">
            {/* Section Header */}
            <div className="relative">
              <div className="absolute -left-4 top-0 w-1 h-full bg-gradient-to-b from-[#00677F] to-[#7B68EE] rounded-full" />
              <h3 className="text-2xl font-light text-white tracking-tight">Contact Information</h3>
              <p className="text-slate-500 text-sm mt-1">Let us know how to reach you</p>
            </div>

            {/* WHO YOU ARE - Glass Card */}
            <div className="backdrop-blur-xl bg-white/[0.02] border border-white/[0.05] rounded-2xl p-6 space-y-5 hover:border-[#00677F]/20 transition-colors duration-500">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-8 h-8 rounded-full bg-[#00677F]/10 flex items-center justify-center">
                  <User className="w-4 h-4 text-[#00677F]" />
                </div>
                <h4 className="text-xs font-medium uppercase tracking-wider text-slate-500">Who You Are</h4>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs font-medium uppercase tracking-wider text-slate-400">
                    First Name <span className="text-[#A1D8E0]">*</span>
                  </Label>
                  <Input
                    value={personalInfo.first_name || ""}
                    onChange={(e) => setPersonalInfo({ ...personalInfo, first_name: e.target.value })}
                    className="h-12 bg-black/30 border-white/[0.08] focus:border-[#A1D8E0] focus:ring-2 focus:ring-[#A1D8E0]/20 text-white rounded-full px-5 transition-all duration-300 placeholder:text-slate-600"
                    placeholder="Enter first name"
                    data-testid="contact-firstname"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-medium uppercase tracking-wider text-slate-400">Middle Name</Label>
                  <Input
                    value={personalInfo.middle_name || ""}
                    onChange={(e) => setPersonalInfo({ ...personalInfo, middle_name: e.target.value })}
                    className="h-12 bg-black/30 border-white/[0.08] focus:border-[#A1D8E0] focus:ring-2 focus:ring-[#A1D8E0]/20 text-white rounded-full px-5 transition-all duration-300 placeholder:text-slate-600"
                    placeholder="Optional"
                    data-testid="contact-middlename"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-medium uppercase tracking-wider text-slate-400">
                    Last Name <span className="text-[#A1D8E0]">*</span>
                  </Label>
                  <Input
                    value={personalInfo.last_name || ""}
                    onChange={(e) => setPersonalInfo({ ...personalInfo, last_name: e.target.value })}
                    className="h-12 bg-black/30 border-white/[0.08] focus:border-[#A1D8E0] focus:ring-2 focus:ring-[#A1D8E0]/20 text-white rounded-full px-5 transition-all duration-300 placeholder:text-slate-600"
                    placeholder="Enter last name"
                    data-testid="contact-lastname"
                  />
                </div>
              </div>
              
              <div className="max-w-xs">
                <Label className="text-xs font-medium uppercase tracking-wider text-slate-400">Preferred Name</Label>
                <p className="text-slate-600 text-xs mb-2">What should we call you?</p>
                <Input
                  value={personalInfo.preferred_name || ""}
                  onChange={(e) => setPersonalInfo({ ...personalInfo, preferred_name: e.target.value })}
                  className="h-12 bg-black/30 border-white/[0.08] focus:border-[#A1D8E0] focus:ring-2 focus:ring-[#A1D8E0]/20 text-white rounded-full px-5 transition-all duration-300 placeholder:text-slate-600"
                  placeholder="Nickname or preferred name"
                  data-testid="contact-preferredname"
                />
              </div>
            </div>

            {/* HOW TO REACH YOU - Glass Card */}
            <div className="backdrop-blur-xl bg-white/[0.02] border border-white/[0.05] rounded-2xl p-6 space-y-5 hover:border-[#7B68EE]/20 transition-colors duration-500">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-8 h-8 rounded-full bg-[#7B68EE]/10 flex items-center justify-center">
                  <Phone className="w-4 h-4 text-[#7B68EE]" />
                </div>
                <h4 className="text-xs font-medium uppercase tracking-wider text-slate-500">How to Reach You</h4>
              </div>
              
              <div className="space-y-2">
                <Label className="text-xs font-medium uppercase tracking-wider text-slate-400">
                  Email Address <span className="text-[#A1D8E0]">*</span>
                </Label>
                <p className="text-slate-600 text-xs">We'll send application updates here</p>
                <Input
                  type="email"
                  value={personalInfo.email || ""}
                  onChange={(e) => setPersonalInfo({ ...personalInfo, email: e.target.value })}
                  className="h-12 bg-black/30 border-white/[0.08] focus:border-[#A1D8E0] focus:ring-2 focus:ring-[#A1D8E0]/20 text-white rounded-full px-5 transition-all duration-300 placeholder:text-slate-600 max-w-md"
                  placeholder="you@example.com"
                  data-testid="contact-email"
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl">
                <div className="space-y-2">
                  <Label className="text-xs font-medium uppercase tracking-wider text-slate-400">
                    Phone Number <span className="text-[#A1D8E0]">*</span>
                  </Label>
                  <PhoneInputWithCode
                    value={personalInfo.phone || ""}
                    countryCode={personalInfo.phone_country_code || "+1|US"}
                    onPhoneChange={(val) => setPersonalInfo({ ...personalInfo, phone: val })}
                    onCodeChange={(val) => setPersonalInfo({ ...personalInfo, phone_country_code: val })}
                    testId="contact-phone"
                    theme={theme}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-medium uppercase tracking-wider text-slate-400">Mobile Phone</Label>
                  <PhoneInputWithCode
                    value={personalInfo.alternate_phone || ""}
                    countryCode={personalInfo.alt_phone_country_code || "+1|US"}
                    onPhoneChange={(val) => setPersonalInfo({ ...personalInfo, alternate_phone: val })}
                    onCodeChange={(val) => setPersonalInfo({ ...personalInfo, alt_phone_country_code: val })}
                    testId="contact-altphone"
                    theme={theme}
                  />
                </div>
              </div>
            </div>

            {/* WHERE YOU LIVE - Glass Card */}
            <div className="backdrop-blur-xl bg-white/[0.02] border border-white/[0.05] rounded-2xl p-6 space-y-5 hover:border-[#739600]/20 transition-colors duration-500">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-8 h-8 rounded-full bg-[#739600]/10 flex items-center justify-center">
                  <svg className="w-4 h-4 text-[#739600]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <h4 className="text-xs font-medium uppercase tracking-wider text-slate-500">Where You Live</h4>
              </div>

              {/* Country Selection - Asked FIRST */}
              <div className="space-y-2 max-w-xs">
                <Label className="text-xs font-medium uppercase tracking-wider text-slate-400">Country <span className="text-[#A1D8E0]">*</span></Label>
                <Select
                  value={personalInfo.country || "United States"}
                  onValueChange={(value) => {
                    const updates = { country: value };
                    if (value !== "United States") {
                      updates.state = "";
                    }
                    setPersonalInfo({ ...personalInfo, ...updates });
                  }}
                >
                  <SelectTrigger className="h-12 bg-black/30 border-white/[0.08] text-white rounded-full px-5 focus:border-[#A1D8E0] focus:ring-2 focus:ring-[#A1D8E0]/20" data-testid="contact-country">
                    <SelectValue placeholder="Select country" />
                  </SelectTrigger>
                  <SelectContent className="rounded-xl bg-[#11161F] border-white/10 max-h-60">
                    <SelectItem value="United States">United States</SelectItem>
                    <SelectItem value="Canada">Canada</SelectItem>
                    <SelectItem value="United Kingdom">United Kingdom</SelectItem>
                    <SelectItem value="India">India</SelectItem>
                    <SelectItem value="Australia">Australia</SelectItem>
                    <SelectItem value="Germany">Germany</SelectItem>
                    <SelectItem value="France">France</SelectItem>
                    <SelectItem value="China">China</SelectItem>
                    <SelectItem value="Japan">Japan</SelectItem>
                    <SelectItem value="South Korea">South Korea</SelectItem>
                    <SelectItem value="Brazil">Brazil</SelectItem>
                    <SelectItem value="Mexico">Mexico</SelectItem>
                    <SelectItem value="Nigeria">Nigeria</SelectItem>
                    <SelectItem value="South Africa">South Africa</SelectItem>
                    <SelectItem value="Philippines">Philippines</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-xs font-medium uppercase tracking-wider text-slate-400">Street Address</Label>
                <Input
                  value={personalInfo.address || ""}
                  onChange={(e) => setPersonalInfo({ ...personalInfo, address: e.target.value })}
                  className="h-12 bg-black/30 border-white/[0.08] focus:border-[#A1D8E0] focus:ring-2 focus:ring-[#A1D8E0]/20 text-white rounded-full px-5 transition-all duration-300 placeholder:text-slate-600"
                  placeholder="123 Main Street, Apt 4B"
                  data-testid="contact-address"
                />
              </div>

              {(personalInfo.country || "United States") === "United States" ? (
                /* USA: Zip Code → auto-populates City & State */
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs font-medium uppercase tracking-wider text-slate-400">ZIP Code <span className="text-[#A1D8E0]">*</span></Label>
                    <Input
                      inputMode="numeric"
                      value={personalInfo.zip_code || ""}
                      onChange={async (e) => {
                        const zip = e.target.value.replace(/\D/g, "").slice(0, 5);
                        setPersonalInfo(prev => ({ ...prev, zip_code: zip }));
                        if (zip.length === 5) {
                          try {
                            const resp = await fetch(`https://api.zippopotam.us/us/${zip}`);
                            if (resp.ok) {
                              const data = await resp.json();
                              const place = data.places?.[0];
                              if (place) {
                                setPersonalInfo(prev => ({
                                  ...prev,
                                  zip_code: zip,
                                  city: place["place name"],
                                  state: place["state abbreviation"],
                                }));
                              }
                            }
                          } catch (_) { /* ignore lookup failures */ }
                        }
                      }}
                      className="h-12 bg-black/30 border-white/[0.08] focus:border-[#A1D8E0] focus:ring-2 focus:ring-[#A1D8E0]/20 text-white rounded-full px-5 transition-all duration-300 placeholder:text-slate-600"
                      placeholder="32084"
                      data-testid="contact-zip"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-medium uppercase tracking-wider text-slate-400">City</Label>
                    <Input
                      value={personalInfo.city || ""}
                      onChange={(e) => setPersonalInfo({ ...personalInfo, city: e.target.value })}
                      className="h-12 bg-black/30 border-white/[0.08] focus:border-[#A1D8E0] focus:ring-2 focus:ring-[#A1D8E0]/20 text-white rounded-full px-5 transition-all duration-300 placeholder:text-slate-600"
                      placeholder="Auto-fills from ZIP"
                      data-testid="contact-city"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-medium uppercase tracking-wider text-slate-400">State</Label>
                    <Input
                      value={personalInfo.state || ""}
                      onChange={(e) => setPersonalInfo({ ...personalInfo, state: e.target.value })}
                      className="h-12 bg-black/30 border-white/[0.08] focus:border-[#A1D8E0] focus:ring-2 focus:ring-[#A1D8E0]/20 text-white rounded-full px-5 transition-all duration-300 placeholder:text-slate-600"
                      placeholder="FL"
                      data-testid="contact-state"
                    />
                  </div>
                </div>
              ) : (
                /* Non-USA: City + Zip/Postal Code (no State) */
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl">
                  <div className="space-y-2">
                    <Label className="text-xs font-medium uppercase tracking-wider text-slate-400">City</Label>
                    <Input
                      value={personalInfo.city || ""}
                      onChange={(e) => setPersonalInfo({ ...personalInfo, city: e.target.value })}
                      className="h-12 bg-black/30 border-white/[0.08] focus:border-[#A1D8E0] focus:ring-2 focus:ring-[#A1D8E0]/20 text-white rounded-full px-5 transition-all duration-300 placeholder:text-slate-600"
                      placeholder="Enter your city"
                      data-testid="contact-city"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-medium uppercase tracking-wider text-slate-400">Zip / Postal Code</Label>
                    <Input
                      value={personalInfo.zip_code || ""}
                      onChange={(e) => setPersonalInfo({ ...personalInfo, zip_code: e.target.value })}
                      className="h-12 bg-black/30 border-white/[0.08] focus:border-[#A1D8E0] focus:ring-2 focus:ring-[#A1D8E0]/20 text-white rounded-full px-5 transition-all duration-300 placeholder:text-slate-600"
                      placeholder="Postal code"
                      data-testid="contact-zip"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Completion Indicator */}
            <div className="flex items-center justify-between p-4 rounded-xl bg-[#00677F]/5 border border-[#00677F]/10">
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 rounded-full bg-[#A1D8E0] animate-pulse" />
                <span className="text-sm text-slate-400">
                  {personalInfo.first_name && personalInfo.last_name && personalInfo.email && personalInfo.phone
                    ? "All required fields completed"
                    : "Complete required fields to continue"}
                </span>
              </div>
              <span className="text-xs font-medium text-[#A1D8E0]">
                {[personalInfo.first_name, personalInfo.last_name, personalInfo.email, personalInfo.phone].filter(Boolean).length}/4 required
              </span>
            </div>
          </div>
        );

      case 1:
        return (
          <div className="space-y-8" data-testid="subsection-judicial">
            {/* Section Header */}
            <div className="relative">
              <div className="absolute -left-4 top-0 w-1 h-full bg-gradient-to-b from-[#FF6B35] to-[#FF8C5A] rounded-full" />
              <h3 className="text-2xl font-light text-white tracking-tight">Judicial Background</h3>
              <p className="text-slate-500 text-sm mt-1">Required background check disclosure</p>
            </div>

            {/* Background Check Agreement - Glass Card */}
            <div className="backdrop-blur-xl bg-white/[0.02] border border-white/[0.05] rounded-2xl p-6 space-y-5 hover:border-[#FF6B35]/20 transition-colors duration-500">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-8 h-8 rounded-full bg-[#FF6B35]/10 flex items-center justify-center">
                  <Shield className="w-4 h-4 text-[#FF6B35]" />
                </div>
                <h4 className="text-xs font-medium uppercase tracking-wider text-slate-500">Background Check Agreement</h4>
              </div>
              
              <div className="p-4 rounded-xl bg-[#FF6B35]/5 border border-[#FF6B35]/10">
                <p className="text-slate-300 text-sm leading-relaxed">
                  All applicants are required to complete a criminal background check as part of the admission process. 
                  Clinical affiliates may require additional background checks prior to clinical placement. 
                  Certain criminal convictions may affect eligibility for licensure in your chosen field.
                </p>
              </div>
              
              <RadioGroup
                value={personalInfo.judicial_agreement}
                onValueChange={(value) => {
                  if (value === "disagree") {
                    setShowBgCheckModal(true);
                  } else {
                    setPersonalInfo({ ...personalInfo, judicial_agreement: value });
                  }
                }}
                className="space-y-3"
              >
                <div className={`flex items-center space-x-3 p-4 rounded-full border transition-all duration-300 cursor-pointer ${
                  personalInfo.judicial_agreement === "agree" 
                    ? "border-[#739600]/50 bg-[#739600]/10" 
                    : "border-white/[0.08] bg-black/30 hover:border-[#00677F]/30"
                }`}>
                  <RadioGroupItem value="agree" id="agree" className="border-[#00677F]" />
                  <Label htmlFor="agree" className="text-white cursor-pointer flex-1 text-sm">
                    I Agree - I understand and consent to the background check requirements
                  </Label>
                  {personalInfo.judicial_agreement === "agree" && <Check className="w-4 h-4 text-[#739600]" />}
                </div>
                <div className={`flex items-center space-x-3 p-4 rounded-full border transition-all duration-300 cursor-pointer ${
                  personalInfo.judicial_agreement === "disagree" 
                    ? "border-[#FF6B35]/50 bg-[#FF6B35]/10" 
                    : "border-white/[0.08] bg-black/30 hover:border-[#00677F]/30"
                }`}>
                  <RadioGroupItem value="disagree" id="disagree" className="border-[#00677F]" />
                  <Label htmlFor="disagree" className="text-white cursor-pointer flex-1 text-sm">
                    I Do Not Agree
                  </Label>
                </div>
              </RadioGroup>
            </div>

            {/* Background Check Disclosure Modal */}
            {showBgCheckModal && (
              <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="bg-check-disclosure-modal">
                <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
                <div className="relative w-full max-w-lg rounded-2xl overflow-hidden shadow-2xl shadow-black/50 animate-in fade-in zoom-in-95 duration-200">
                  {/* Header */}
                  <div className="relative px-6 py-5 bg-gradient-to-r from-[#DC5034] to-[#FF6B35]">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center">
                        <AlertTriangle className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h4 className="text-white text-lg font-semibold">Background Check Required</h4>
                        <p className="text-white/70 text-xs">Disclosure Notice</p>
                      </div>
                    </div>
                  </div>

                  {/* Body */}
                  <div className="bg-[#0f1419] px-6 py-6 space-y-5">
                    <p className="text-slate-200 text-sm leading-relaxed">
                      In order to move forward with your application, you <strong className="text-white">must agree to the background check requirement</strong>. 
                      This is a mandatory step in the admissions process for the University of St. Augustine for Health Sciences.
                    </p>
                    <div className="p-4 rounded-xl bg-[#FF6B35]/5 border border-[#FF6B35]/15">
                      <p className="text-slate-300 text-sm leading-relaxed">
                        All applicants are required to complete a criminal background check as part of the admission process. 
                        Clinical affiliates may require additional background checks prior to clinical placement. 
                        Certain criminal convictions may affect eligibility for licensure in your chosen field.
                      </p>
                    </div>
                    <p className="text-slate-400 text-sm leading-relaxed">
                      If you choose not to consent to a background check, your application <strong className="text-slate-200">cannot be processed</strong> and 
                      you will not be able to proceed to the next steps. Please contact your Enrollment Advisor if you have any questions or concerns.
                    </p>
                  </div>

                  {/* Footer */}
                  <div className="bg-[#0f1419] border-t border-white/[0.06] px-6 pb-6 flex justify-end gap-3">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setPersonalInfo({ ...personalInfo, judicial_agreement: "disagree" });
                        setShowBgCheckModal(false);
                        if (onBgCheckDeclined) onBgCheckDeclined();
                      }}
                      className="border-white/20 text-slate-300 hover:bg-white/5 rounded-xl px-5"
                      data-testid="bg-check-decline-btn"
                    >
                      I Still Do Not Agree
                    </Button>
                    <Button
                      onClick={() => {
                        setPersonalInfo({ ...personalInfo, judicial_agreement: "agree" });
                        setShowBgCheckModal(false);
                      }}
                      className="bg-[#739600] hover:bg-[#5e7a00] text-white rounded-xl px-6 shadow-[0_0_15px_rgba(115,150,0,0.3)]"
                      data-testid="bg-check-agree-btn"
                    >
                      I Agree to Background Check
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Criminal History - Glass Card */}
            <div className="backdrop-blur-xl bg-white/[0.02] border border-white/[0.05] rounded-2xl p-6 space-y-5 hover:border-[#7B68EE]/20 transition-colors duration-500">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-8 h-8 rounded-full bg-[#7B68EE]/10 flex items-center justify-center">
                  <svg className="w-4 h-4 text-[#7B68EE]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <h4 className="text-xs font-medium uppercase tracking-wider text-slate-500">Criminal History Disclosure</h4>
              </div>

              <div className="space-y-3">
                <Label className="text-xs font-medium uppercase tracking-wider text-slate-400">
                  Have you ever been convicted of a felony? <span className="text-[#A1D8E0]">*</span>
                </Label>
                <RadioGroup
                  value={personalInfo.felony_conviction}
                  onValueChange={(value) => setPersonalInfo({ ...personalInfo, felony_conviction: value })}
                  className="flex gap-4"
                >
                  <div className={`flex items-center space-x-2 px-6 py-3 rounded-full border transition-all duration-300 cursor-pointer ${
                    personalInfo.felony_conviction === "yes" 
                      ? "border-[#A1D8E0]/50 bg-[#A1D8E0]/10" 
                      : "border-white/[0.08] bg-black/30 hover:border-white/20"
                  }`}>
                    <RadioGroupItem value="yes" id="felony-yes" />
                    <Label htmlFor="felony-yes" className="text-white cursor-pointer">Yes</Label>
                  </div>
                  <div className={`flex items-center space-x-2 px-6 py-3 rounded-full border transition-all duration-300 cursor-pointer ${
                    personalInfo.felony_conviction === "no" 
                      ? "border-[#A1D8E0]/50 bg-[#A1D8E0]/10" 
                      : "border-white/[0.08] bg-black/30 hover:border-white/20"
                  }`}>
                    <RadioGroupItem value="no" id="felony-no" />
                    <Label htmlFor="felony-no" className="text-white cursor-pointer">No</Label>
                  </div>
                </RadioGroup>
              </div>

              {personalInfo.felony_conviction === "yes" && (
                <div className="space-y-2 pt-2 animate-in slide-in-from-top-2 duration-300">
                  <Label className="text-xs font-medium uppercase tracking-wider text-slate-400">
                    Please explain the circumstances
                  </Label>
                  <p className="text-slate-600 text-xs">This information will be reviewed confidentially</p>
                  <Textarea
                    value={personalInfo.felony_explanation || ""}
                    onChange={(e) => setPersonalInfo({ ...personalInfo, felony_explanation: e.target.value })}
                    placeholder="Provide details about the conviction..."
                    className="min-h-[120px] bg-black/30 border-white/[0.08] focus:border-[#A1D8E0] focus:ring-2 focus:ring-[#A1D8E0]/20 text-white rounded-2xl resize-none transition-all duration-300 placeholder:text-slate-600"
                    data-testid="felony-explanation"
                  />
                </div>
              )}
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-8" data-testid="subsection-emergency">
            {/* Section Header */}
            <div className="relative">
              <div className="absolute -left-4 top-0 w-1 h-full bg-gradient-to-b from-[#E91E63] to-[#F48FB1] rounded-full" />
              <h3 className="text-2xl font-light text-white tracking-tight">Emergency Contact</h3>
              <p className="text-slate-500 text-sm mt-1">Someone we can reach in case of emergency</p>
            </div>

            {/* Contact Person - Glass Card */}
            <div className="backdrop-blur-xl bg-white/[0.02] border border-white/[0.05] rounded-2xl p-6 space-y-5 hover:border-[#E91E63]/20 transition-colors duration-500">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-8 h-8 rounded-full bg-[#E91E63]/10 flex items-center justify-center">
                  <Heart className="w-4 h-4 text-[#E91E63]" />
                </div>
                <h4 className="text-xs font-medium uppercase tracking-wider text-slate-500">Contact Person</h4>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs font-medium uppercase tracking-wider text-slate-400">
                    First Name <span className="text-[#A1D8E0]">*</span>
                  </Label>
                  <Input
                    value={personalInfo.emergency_first_name || ""}
                    onChange={(e) => setPersonalInfo({ ...personalInfo, emergency_first_name: e.target.value })}
                    className="h-12 bg-black/30 border-white/[0.08] focus:border-[#A1D8E0] focus:ring-2 focus:ring-[#A1D8E0]/20 text-white rounded-full px-5 transition-all duration-300 placeholder:text-slate-600"
                    placeholder="Contact's first name"
                    data-testid="emergency-firstname"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-medium uppercase tracking-wider text-slate-400">
                    Last Name <span className="text-[#A1D8E0]">*</span>
                  </Label>
                  <Input
                    value={personalInfo.emergency_last_name || ""}
                    onChange={(e) => setPersonalInfo({ ...personalInfo, emergency_last_name: e.target.value })}
                    className="h-12 bg-black/30 border-white/[0.08] focus:border-[#A1D8E0] focus:ring-2 focus:ring-[#A1D8E0]/20 text-white rounded-full px-5 transition-all duration-300 placeholder:text-slate-600"
                    placeholder="Contact's last name"
                    data-testid="emergency-lastname"
                  />
                </div>
              </div>

              <div className="space-y-2 max-w-xs">
                <Label className="text-xs font-medium uppercase tracking-wider text-slate-400">
                  Relationship <span className="text-[#A1D8E0]">*</span>
                </Label>
                <Select
                  value={personalInfo.emergency_relationship || ""}
                  onValueChange={(value) => setPersonalInfo({ ...personalInfo, emergency_relationship: value })}
                >
                  <SelectTrigger className="h-12 bg-black/30 border-white/[0.08] text-white rounded-full px-5 focus:border-[#A1D8E0] focus:ring-2 focus:ring-[#A1D8E0]/20" data-testid="emergency-relationship">
                    <SelectValue placeholder="Select relationship" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#11161F] border-white/10 rounded-xl">
                    <SelectItem value="spouse">Spouse / Partner</SelectItem>
                    <SelectItem value="parent">Parent</SelectItem>
                    <SelectItem value="sibling">Sibling</SelectItem>
                    <SelectItem value="child">Child</SelectItem>
                    <SelectItem value="friend">Friend</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Contact Details - Glass Card */}
            <div className="backdrop-blur-xl bg-white/[0.02] border border-white/[0.05] rounded-2xl p-6 space-y-5 hover:border-[#00677F]/20 transition-colors duration-500">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-8 h-8 rounded-full bg-[#00677F]/10 flex items-center justify-center">
                  <Phone className="w-4 h-4 text-[#00677F]" />
                </div>
                <h4 className="text-xs font-medium uppercase tracking-wider text-slate-500">Contact Details</h4>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs font-medium uppercase tracking-wider text-slate-400">
                    Phone Number <span className="text-[#A1D8E0]">*</span>
                  </Label>
                  <PhoneInputWithCode
                    value={personalInfo.emergency_phone || ""}
                    countryCode={personalInfo.emergency_phone_country_code || "+1|US"}
                    onPhoneChange={(val) => setPersonalInfo({ ...personalInfo, emergency_phone: val })}
                    onCodeChange={(val) => setPersonalInfo({ ...personalInfo, emergency_phone_country_code: val })}
                    testId="emergency-phone"
                    theme={theme}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-medium uppercase tracking-wider text-slate-400">Other Phone</Label>
                  <PhoneInputWithCode
                    value={personalInfo.emergency_other_phone || ""}
                    countryCode={personalInfo.emergency_other_phone_country_code || "+1|US"}
                    onPhoneChange={(val) => setPersonalInfo({ ...personalInfo, emergency_other_phone: val })}
                    onCodeChange={(val) => setPersonalInfo({ ...personalInfo, emergency_other_phone_country_code: val })}
                    testId="emergency-other-phone"
                    theme={theme}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs font-medium uppercase tracking-wider text-slate-400">Email Address</Label>
                  <Input
                    type="email"
                    value={personalInfo.emergency_email || ""}
                    onChange={(e) => setPersonalInfo({ ...personalInfo, emergency_email: e.target.value })}
                    placeholder="contact@email.com"
                    className="h-12 bg-black/30 border-white/[0.08] focus:border-[#A1D8E0] focus:ring-2 focus:ring-[#A1D8E0]/20 text-white rounded-full px-5 transition-all duration-300 placeholder:text-slate-600"
                    data-testid="emergency-email"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-medium uppercase tracking-wider text-slate-400">
                    Preferred Method of Contact
                  </Label>
                  <Select
                    value={personalInfo.emergency_preferred_contact || ""}
                    onValueChange={(value) => setPersonalInfo({ ...personalInfo, emergency_preferred_contact: value })}
                  >
                    <SelectTrigger className="h-12 bg-black/30 border-white/[0.08] text-white rounded-full px-5 focus:border-[#A1D8E0] focus:ring-2 focus:ring-[#A1D8E0]/20" data-testid="emergency-preferred-contact">
                      <SelectValue placeholder="Select preferred method" />
                    </SelectTrigger>
                    <SelectContent className="rounded-xl bg-[#11161F] border-white/10">
                      <SelectItem value="phone">Phone</SelectItem>
                      <SelectItem value="email">Email</SelectItem>
                      <SelectItem value="text">Text Message</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Address - separated fields with Country → ZIP logic */}
              <div className="space-y-4 pt-2">
                <Label className="text-xs font-medium uppercase tracking-wider text-slate-400">Address</Label>
                <p className="text-slate-600 text-xs -mt-3">Optional - helpful if they live nearby</p>

                <div className="space-y-2 max-w-xs">
                  <Label className="text-xs font-medium uppercase tracking-wider text-slate-400">Country</Label>
                  <Select
                    value={personalInfo.emergency_country || "United States"}
                    onValueChange={(value) => {
                      const updates = { emergency_country: value };
                      if (value !== "United States") {
                        updates.emergency_state = "";
                      }
                      setPersonalInfo({ ...personalInfo, ...updates });
                    }}
                  >
                    <SelectTrigger className="h-12 bg-black/30 border-white/[0.08] text-white rounded-full px-5 focus:border-[#A1D8E0] focus:ring-2 focus:ring-[#A1D8E0]/20" data-testid="emergency-country">
                      <SelectValue placeholder="Select country" />
                    </SelectTrigger>
                    <SelectContent className="rounded-xl bg-[#11161F] border-white/10 max-h-60">
                      <SelectItem value="United States">United States</SelectItem>
                      <SelectItem value="Canada">Canada</SelectItem>
                      <SelectItem value="United Kingdom">United Kingdom</SelectItem>
                      <SelectItem value="India">India</SelectItem>
                      <SelectItem value="Australia">Australia</SelectItem>
                      <SelectItem value="Germany">Germany</SelectItem>
                      <SelectItem value="France">France</SelectItem>
                      <SelectItem value="China">China</SelectItem>
                      <SelectItem value="Japan">Japan</SelectItem>
                      <SelectItem value="South Korea">South Korea</SelectItem>
                      <SelectItem value="Brazil">Brazil</SelectItem>
                      <SelectItem value="Mexico">Mexico</SelectItem>
                      <SelectItem value="Nigeria">Nigeria</SelectItem>
                      <SelectItem value="South Africa">South Africa</SelectItem>
                      <SelectItem value="Philippines">Philippines</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs font-medium uppercase tracking-wider text-slate-400">Street Address</Label>
                  <Input
                    value={personalInfo.emergency_street || ""}
                    onChange={(e) => setPersonalInfo({ ...personalInfo, emergency_street: e.target.value })}
                    placeholder="123 Main Street, Apt 4B"
                    className="h-12 bg-black/30 border-white/[0.08] focus:border-[#A1D8E0] focus:ring-2 focus:ring-[#A1D8E0]/20 text-white rounded-full px-5 transition-all duration-300 placeholder:text-slate-600"
                    data-testid="emergency-street"
                  />
                </div>

                {(personalInfo.emergency_country || "United States") === "United States" ? (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label className="text-xs font-medium uppercase tracking-wider text-slate-400">ZIP Code</Label>
                      <Input
                        inputMode="numeric"
                        value={personalInfo.emergency_zip || ""}
                        onChange={async (e) => {
                          const zip = e.target.value.replace(/\D/g, "").slice(0, 5);
                          setPersonalInfo(prev => ({ ...prev, emergency_zip: zip }));
                          if (zip.length === 5) {
                            try {
                              const resp = await fetch(`https://api.zippopotam.us/us/${zip}`);
                              if (resp.ok) {
                                const data = await resp.json();
                                const place = data.places?.[0];
                                if (place) {
                                  setPersonalInfo(prev => ({
                                    ...prev,
                                    emergency_zip: zip,
                                    emergency_city: place["place name"],
                                    emergency_state: place["state abbreviation"],
                                  }));
                                }
                              }
                            } catch (_) {}
                          }
                        }}
                        className="h-12 bg-black/30 border-white/[0.08] focus:border-[#A1D8E0] focus:ring-2 focus:ring-[#A1D8E0]/20 text-white rounded-full px-5 transition-all duration-300 placeholder:text-slate-600"
                        placeholder="32084"
                        data-testid="emergency-zip"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs font-medium uppercase tracking-wider text-slate-400">City</Label>
                      <Input
                        value={personalInfo.emergency_city || ""}
                        onChange={(e) => setPersonalInfo({ ...personalInfo, emergency_city: e.target.value })}
                        className="h-12 bg-black/30 border-white/[0.08] focus:border-[#A1D8E0] focus:ring-2 focus:ring-[#A1D8E0]/20 text-white rounded-full px-5 transition-all duration-300 placeholder:text-slate-600"
                        placeholder="Auto-fills from ZIP"
                        data-testid="emergency-city"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs font-medium uppercase tracking-wider text-slate-400">State</Label>
                      <Input
                        value={personalInfo.emergency_state || ""}
                        onChange={(e) => setPersonalInfo({ ...personalInfo, emergency_state: e.target.value })}
                        className="h-12 bg-black/30 border-white/[0.08] focus:border-[#A1D8E0] focus:ring-2 focus:ring-[#A1D8E0]/20 text-white rounded-full px-5 transition-all duration-300 placeholder:text-slate-600"
                        placeholder="FL"
                        data-testid="emergency-state"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl">
                    <div className="space-y-2">
                      <Label className="text-xs font-medium uppercase tracking-wider text-slate-400">City</Label>
                      <Input
                        value={personalInfo.emergency_city || ""}
                        onChange={(e) => setPersonalInfo({ ...personalInfo, emergency_city: e.target.value })}
                        className="h-12 bg-black/30 border-white/[0.08] focus:border-[#A1D8E0] focus:ring-2 focus:ring-[#A1D8E0]/20 text-white rounded-full px-5 transition-all duration-300 placeholder:text-slate-600"
                        placeholder="Enter city"
                        data-testid="emergency-city"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs font-medium uppercase tracking-wider text-slate-400">Zip / Postal Code</Label>
                      <Input
                        value={personalInfo.emergency_zip || ""}
                        onChange={(e) => setPersonalInfo({ ...personalInfo, emergency_zip: e.target.value })}
                        className="h-12 bg-black/30 border-white/[0.08] focus:border-[#A1D8E0] focus:ring-2 focus:ring-[#A1D8E0]/20 text-white rounded-full px-5 transition-all duration-300 placeholder:text-slate-600"
                        placeholder="Postal code"
                        data-testid="emergency-zip"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Completion Indicator */}
            <div className="flex items-center justify-between p-4 rounded-xl bg-[#E91E63]/5 border border-[#E91E63]/10">
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 rounded-full bg-[#E91E63] animate-pulse" />
                <span className="text-sm text-slate-400">
                  {personalInfo.emergency_first_name && personalInfo.emergency_phone
                    ? "Emergency contact information complete"
                    : "Please provide emergency contact details"}
                </span>
              </div>
              <span className="text-xs font-medium text-[#E91E63]">
                {[personalInfo.emergency_first_name, personalInfo.emergency_last_name, personalInfo.emergency_phone, personalInfo.emergency_relationship].filter(Boolean).length}/4 required
              </span>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-8" data-testid="subsection-citizenship">
            {/* Section Header */}
            <div className="relative">
              <div className="absolute -left-4 top-0 w-1 h-full bg-gradient-to-b from-[#3F51B5] to-[#7986CB] rounded-full" />
              <h3 className="text-2xl font-light text-white tracking-tight">Citizenship & Identification</h3>
              <p className="text-slate-500 text-sm mt-1">Identity verification and residency information</p>
            </div>

            {/* Personal Details - Glass Card */}
            <div className="backdrop-blur-xl bg-white/[0.02] border border-white/[0.05] rounded-2xl p-6 space-y-5 hover:border-[#3F51B5]/20 transition-colors duration-500">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-8 h-8 rounded-full bg-[#3F51B5]/10 flex items-center justify-center">
                  <User className="w-4 h-4 text-[#3F51B5]" />
                </div>
                <h4 className="text-xs font-medium uppercase tracking-wider text-slate-500">Personal Details</h4>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="text-xs font-medium uppercase tracking-wider text-slate-400">
                    Date of Birth <span className="text-[#A1D8E0]">*</span>
                  </Label>
                  {(() => {
                    const existingDob = personalInfo.date_of_birth || "";
                    let dobMonth = "", dobDay = "", dobYear = "";
                    if (existingDob) {
                      const parts = existingDob.split("-");
                      if (parts.length === 3) {
                        dobYear = parts[0];
                        dobMonth = String(parseInt(parts[1]));
                        dobDay = String(parseInt(parts[2]));
                      }
                    }
                    const handleDobChange = (field, value) => {
                      let m = dobMonth, d = dobDay, y = dobYear;
                      if (field === "month") m = value;
                      if (field === "day") d = value;
                      if (field === "year") y = value;
                      if (m && d && y) {
                        const dob = `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
                        const birth = new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
                        const today = new Date();
                        let age = today.getFullYear() - birth.getFullYear();
                        const monthDiff = today.getMonth() - birth.getMonth();
                        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
                          age--;
                        }
                        if (age < 10) {
                          setPendingDob(dob);
                          setShowAgeVerifyModal(true);
                          return;
                        }
                        setPersonalInfo({ ...personalInfo, date_of_birth: dob });
                      } else {
                        const partial = `${y || ""}-${m ? m.padStart(2, "0") : ""}-${d ? d.padStart(2, "0") : ""}`;
                        setPersonalInfo({ ...personalInfo, date_of_birth: (m || d || y) ? partial : "" });
                      }
                    };
                    const months = [
                      { value: "1", label: "January" }, { value: "2", label: "February" },
                      { value: "3", label: "March" }, { value: "4", label: "April" },
                      { value: "5", label: "May" }, { value: "6", label: "June" },
                      { value: "7", label: "July" }, { value: "8", label: "August" },
                      { value: "9", label: "September" }, { value: "10", label: "October" },
                      { value: "11", label: "November" }, { value: "12", label: "December" }
                    ];
                    const currentYear = new Date().getFullYear();
                    const years = Array.from({ length: 100 }, (_, i) => currentYear - i);
                    const daysCount = (dobMonth && dobYear) ? new Date(parseInt(dobYear), parseInt(dobMonth), 0).getDate() : 31;
                    const days = Array.from({ length: daysCount }, (_, i) => i + 1);
                    return (
                      <div className="grid grid-cols-3 gap-2" data-testid="citizenship-dob">
                        <Select value={dobMonth} onValueChange={(v) => handleDobChange("month", v)} disabled={isSubmitted}>
                          <SelectTrigger className="h-12 bg-black/30 border-white/[0.08] focus:border-[#A1D8E0] text-white rounded-full px-4 transition-all duration-300" data-testid="dob-month">
                            <SelectValue placeholder="Month" />
                          </SelectTrigger>
                          <SelectContent className="bg-[#0f1419] border-white/10 text-white max-h-60">
                            {months.map((m) => (
                              <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Select value={dobDay} onValueChange={(v) => handleDobChange("day", v)} disabled={isSubmitted}>
                          <SelectTrigger className="h-12 bg-black/30 border-white/[0.08] focus:border-[#A1D8E0] text-white rounded-full px-4 transition-all duration-300" data-testid="dob-day">
                            <SelectValue placeholder="Day" />
                          </SelectTrigger>
                          <SelectContent className="bg-[#0f1419] border-white/10 text-white max-h-60">
                            {days.map((d) => (
                              <SelectItem key={d} value={String(d)}>{d}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Select value={dobYear} onValueChange={(v) => handleDobChange("year", v)} disabled={isSubmitted}>
                          <SelectTrigger className="h-12 bg-black/30 border-white/[0.08] focus:border-[#A1D8E0] text-white rounded-full px-4 transition-all duration-300" data-testid="dob-year">
                            <SelectValue placeholder="Year" />
                          </SelectTrigger>
                          <SelectContent className="bg-[#0f1419] border-white/10 text-white max-h-60">
                            {years.map((y) => (
                              <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    );
                  })()}
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-medium uppercase tracking-wider text-slate-400">
                    What pronouns do you use? <span className="text-[#A1D8E0]">*</span>
                  </Label>
                  <RadioGroup
                    value={personalInfo.gender || ""}
                    onValueChange={(value) => setPersonalInfo({ ...personalInfo, gender: value })}
                    className="flex flex-wrap gap-3 items-center"
                  >
                    {[
                      { value: "he_him", label: "He / Him" },
                      { value: "she_her", label: "She / Her" },
                      { value: "they_them", label: "They / Them" },
                      { value: "prefer_not_to_say", label: "Prefer not to say" }
                    ].map((option) => (
                      <div key={option.value} className={`flex items-center space-x-2 px-4 py-2 rounded-full border transition-all duration-300 cursor-pointer ${
                        personalInfo.gender === option.value 
                          ? "border-[#A1D8E0]/50 bg-[#A1D8E0]/10" 
                          : "border-white/[0.08] bg-black/30 hover:border-white/20"
                      }`}>
                        <RadioGroupItem value={option.value} id={`pronoun-${option.value}`} />
                        <Label htmlFor={`pronoun-${option.value}`} className="text-white cursor-pointer text-sm">{option.label}</Label>
                      </div>
                    ))}
                  </RadioGroup>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-xs font-medium uppercase tracking-wider text-slate-400">
                  Gender <span className="text-[#A1D8E0]">*</span>
                </Label>
                <RadioGroup
                  value={personalInfo.sex || ""}
                  onValueChange={(value) => setPersonalInfo({ ...personalInfo, sex: value })}
                  className="flex flex-wrap gap-3 items-center"
                  data-testid="citizenship-gender"
                >
                  {[
                    { value: "male", label: "Male" },
                    { value: "female", label: "Female" }
                  ].map((option) => (
                    <div key={option.value} className={`flex items-center space-x-2 px-4 py-2 rounded-full border transition-all duration-300 cursor-pointer ${
                      personalInfo.sex === option.value 
                        ? "border-[#A1D8E0]/50 bg-[#A1D8E0]/10" 
                        : "border-white/[0.08] bg-black/30 hover:border-white/20"
                    }`}>
                      <RadioGroupItem value={option.value} id={`gender-${option.value}`} data-testid={`gender-${option.value}`} />
                      <Label htmlFor={`gender-${option.value}`} className="text-white cursor-pointer text-sm">{option.label}</Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>

              <div className="space-y-2">
                <Label className="text-xs font-medium uppercase tracking-wider text-slate-400">Marital Status</Label>
                <RadioGroup
                  value={personalInfo.marital_status || ""}
                  onValueChange={(value) => setPersonalInfo({ ...personalInfo, marital_status: value })}
                  className="flex gap-3"
                >
                  {[
                    { value: "single", label: "Single" },
                    { value: "married", label: "Married" },
                    { value: "widowed", label: "Widowed" },
                    { value: "divorced", label: "Divorced" }
                  ].map((option) => (
                    <div key={option.value} className={`flex items-center space-x-2 px-4 py-2 rounded-full border transition-all duration-300 cursor-pointer ${
                      personalInfo.marital_status === option.value 
                        ? "border-[#A1D8E0]/50 bg-[#A1D8E0]/10" 
                        : "border-white/[0.08] bg-black/30 hover:border-white/20"
                    }`}>
                      <RadioGroupItem value={option.value} id={option.value} />
                      <Label htmlFor={option.value} className="text-white cursor-pointer text-sm">{option.label}</Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>
            </div>

            {/* Citizenship Status - Glass Card */}
            <div className="backdrop-blur-xl bg-white/[0.02] border border-white/[0.05] rounded-2xl p-6 space-y-5 hover:border-[#00677F]/20 transition-colors duration-500">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-8 h-8 rounded-full bg-[#00677F]/10 flex items-center justify-center">
                  <Flag className="w-4 h-4 text-[#00677F]" />
                </div>
                <h4 className="text-xs font-medium uppercase tracking-wider text-slate-500">Citizenship Status</h4>
              </div>
              
              <div className="space-y-3">
                <Label className="text-xs font-medium uppercase tracking-wider text-slate-400">
                  Are you a U.S. Citizen? <span className="text-[#A1D8E0]">*</span>
                </Label>
                <RadioGroup
                  value={personalInfo.us_citizen || ""}
                  onValueChange={(value) => setPersonalInfo({ ...personalInfo, us_citizen: value })}
                  className="flex gap-4"
                >
                  {[{ value: "yes", label: "Yes" }, { value: "no", label: "No" }].map((option) => (
                    <div key={option.value} className={`flex items-center space-x-2 px-6 py-3 rounded-full border transition-all duration-300 cursor-pointer ${
                      personalInfo.us_citizen === option.value 
                        ? "border-[#A1D8E0]/50 bg-[#A1D8E0]/10" 
                        : "border-white/[0.08] bg-black/30 hover:border-white/20"
                    }`}>
                      <RadioGroupItem value={option.value} id={`citizen-${option.value}`} />
                      <Label htmlFor={`citizen-${option.value}`} className="text-white cursor-pointer">{option.label}</Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>

              {personalInfo.us_citizen === "no" && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 animate-in slide-in-from-top-2 duration-300">
                <div className="space-y-2">
                  <Label className="text-xs font-medium uppercase tracking-wider text-slate-400">Residency Status</Label>
                  <Select
                    value={personalInfo.residency_status || ""}
                    onValueChange={(value) => setPersonalInfo({ ...personalInfo, residency_status: value })}
                  >
                    <SelectTrigger className="h-12 bg-black/30 border-white/[0.08] text-white rounded-full px-5 focus:border-[#A1D8E0] focus:ring-2 focus:ring-[#A1D8E0]/20" data-testid="residency-status">
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#11161F] border-white/10 rounded-xl">
                      <SelectItem value="permanent_resident">Permanent Resident</SelectItem>
                      <SelectItem value="non_resident_alien">Non-Resident Alien</SelectItem>
                      <SelectItem value="refugee">Refugee/Asylee</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-medium uppercase tracking-wider text-slate-400">Country of Citizenship</Label>
                  <Select
                    value={personalInfo.country_of_citizenship || ""}
                    onValueChange={(value) => setPersonalInfo({ ...personalInfo, country_of_citizenship: value })}
                  >
                    <SelectTrigger className="h-12 bg-black/30 border-white/[0.08] text-white rounded-full px-5 focus:border-[#A1D8E0] focus:ring-2 focus:ring-[#A1D8E0]/20" data-testid="country-citizenship">
                      <SelectValue placeholder="Select country" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#11161F] border-white/10 rounded-xl max-h-60">
                      {[
                        "Afghanistan","Albania","Algeria","Andorra","Angola","Antigua and Barbuda","Argentina","Armenia","Australia","Austria",
                        "Azerbaijan","Bahamas","Bahrain","Bangladesh","Barbados","Belarus","Belgium","Belize","Benin","Bhutan",
                        "Bolivia","Bosnia and Herzegovina","Botswana","Brazil","Brunei","Bulgaria","Burkina Faso","Burundi","Cabo Verde","Cambodia",
                        "Cameroon","Canada","Central African Republic","Chad","Chile","China","Colombia","Comoros","Congo","Costa Rica",
                        "Croatia","Cuba","Cyprus","Czech Republic","Denmark","Djibouti","Dominica","Dominican Republic","Ecuador","Egypt",
                        "El Salvador","Equatorial Guinea","Eritrea","Estonia","Eswatini","Ethiopia","Fiji","Finland","France","Gabon",
                        "Gambia","Georgia","Germany","Ghana","Greece","Grenada","Guatemala","Guinea","Guinea-Bissau","Guyana",
                        "Haiti","Honduras","Hungary","Iceland","India","Indonesia","Iran","Iraq","Ireland","Israel",
                        "Italy","Jamaica","Japan","Jordan","Kazakhstan","Kenya","Kiribati","Kosovo","Kuwait","Kyrgyzstan",
                        "Laos","Latvia","Lebanon","Lesotho","Liberia","Libya","Liechtenstein","Lithuania","Luxembourg","Madagascar",
                        "Malawi","Malaysia","Maldives","Mali","Malta","Marshall Islands","Mauritania","Mauritius","Mexico","Micronesia",
                        "Moldova","Monaco","Mongolia","Montenegro","Morocco","Mozambique","Myanmar","Namibia","Nauru","Nepal",
                        "Netherlands","New Zealand","Nicaragua","Niger","Nigeria","North Korea","North Macedonia","Norway","Oman","Pakistan",
                        "Palau","Palestine","Panama","Papua New Guinea","Paraguay","Peru","Philippines","Poland","Portugal","Qatar",
                        "Romania","Russia","Rwanda","Saint Kitts and Nevis","Saint Lucia","Saint Vincent and the Grenadines","Samoa","San Marino",
                        "Sao Tome and Principe","Saudi Arabia","Senegal","Serbia","Seychelles","Sierra Leone","Singapore","Slovakia","Slovenia",
                        "Solomon Islands","Somalia","South Africa","South Korea","South Sudan","Spain","Sri Lanka","Sudan","Suriname","Sweden",
                        "Switzerland","Syria","Taiwan","Tajikistan","Tanzania","Thailand","Timor-Leste","Togo","Tonga","Trinidad and Tobago",
                        "Tunisia","Turkey","Turkmenistan","Tuvalu","Uganda","Ukraine","United Arab Emirates","United Kingdom","United States",
                        "Uruguay","Uzbekistan","Vanuatu","Vatican City","Venezuela","Vietnam","Yemen","Zambia","Zimbabwe"
                      ].map((country) => (
                        <SelectItem key={country} value={country.toLowerCase().replace(/\s+/g, '_')}>{country}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              )}

              {personalInfo.us_citizen === "no" && (
                <div className="space-y-3 pt-2 animate-in slide-in-from-top-2 duration-300">
                  <Label className="text-xs font-medium uppercase tracking-wider text-slate-400">Do you have a U.S. Visa?</Label>
                  <RadioGroup
                    value={personalInfo.us_visa || ""}
                    onValueChange={(value) => setPersonalInfo({ ...personalInfo, us_visa: value })}
                    className="flex gap-4"
                  >
                    {[{ value: "yes", label: "Yes" }, { value: "no", label: "No" }].map((option) => (
                      <div key={option.value} className={`flex items-center space-x-2 px-6 py-3 rounded-full border transition-all duration-300 cursor-pointer ${
                        personalInfo.us_visa === option.value 
                          ? "border-[#A1D8E0]/50 bg-[#A1D8E0]/10" 
                          : "border-white/[0.08] bg-black/30 hover:border-white/20"
                      }`}>
                        <RadioGroupItem value={option.value} id={`visa-${option.value}`} />
                        <Label htmlFor={`visa-${option.value}`} className="text-white cursor-pointer">{option.label}</Label>
                      </div>
                    ))}
                  </RadioGroup>
                </div>
              )}
            </div>

            {/* Identification Numbers - Glass Card */}
            <div className="backdrop-blur-xl bg-white/[0.02] border border-white/[0.05] rounded-2xl p-6 space-y-5 hover:border-[#7B68EE]/20 transition-colors duration-500">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-8 h-8 rounded-full bg-[#7B68EE]/10 flex items-center justify-center">
                  <svg className="w-4 h-4 text-[#7B68EE]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                  </svg>
                </div>
                <h4 className="text-xs font-medium uppercase tracking-wider text-slate-500">Identification Numbers</h4>
              </div>
              
              <div className="p-4 rounded-xl bg-[#7B68EE]/5 border border-[#7B68EE]/10">
                <p className="text-slate-400 text-xs">
                  Your SSN/ITIN is encrypted and stored securely. This information is required for financial aid processing.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs font-medium uppercase tracking-wider text-slate-400">Social Security Number (SSN)</Label>
                  <Input
                    value={personalInfo.ssn || ""}
                    onChange={(e) => setPersonalInfo({ ...personalInfo, ssn: e.target.value })}
                    placeholder="000-00-0000"
                    className="h-12 bg-black/30 border-white/[0.08] focus:border-[#A1D8E0] focus:ring-2 focus:ring-[#A1D8E0]/20 text-white rounded-full px-5 transition-all duration-300 placeholder:text-slate-600"
                    data-testid="citizenship-ssn"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-medium uppercase tracking-wider text-slate-400">ITIN (if applicable)</Label>
                  <Input
                    value={personalInfo.itin || ""}
                    onChange={(e) => setPersonalInfo({ ...personalInfo, itin: e.target.value })}
                    placeholder="000-00-0000"
                    className="h-12 bg-black/30 border-white/[0.08] focus:border-[#A1D8E0] focus:ring-2 focus:ring-[#A1D8E0]/20 text-white rounded-full px-5 transition-all duration-300 placeholder:text-slate-600"
                    data-testid="citizenship-itin"
                  />
                </div>
              </div>
            </div>

            {/* Funding Options - Glass Card */}
            <div className="backdrop-blur-xl bg-white/[0.02] border border-white/[0.05] rounded-2xl p-6 space-y-5 hover:border-[#739600]/20 transition-colors duration-500">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-8 h-8 rounded-full bg-[#739600]/10 flex items-center justify-center">
                  <svg className="w-4 h-4 text-[#739600]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h4 className="text-xs font-medium uppercase tracking-wider text-slate-500">Funding Options</h4>
              </div>
              
              <p className="text-slate-500 text-sm">Select all options you're interested in exploring</p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  { id: "federal_loans", label: "Federal Loans", desc: "Government-backed student loans" },
                  { id: "private_loans", label: "Private Loans", desc: "Bank or credit union loans" },
                  { id: "self_pay", label: "Self-Pay", desc: "Pay out of pocket or installments" },
                  { id: "veteran_benefits", label: "Veteran Benefits", desc: "VA education benefits" },
                  { id: "outside_scholarships", label: "Outside Scholarships", desc: "External scholarship programs" },
                ].map((option) => (
                  <div 
                    key={option.id} 
                    className={`flex items-start space-x-3 p-4 rounded-xl border transition-all duration-300 cursor-pointer ${
                      (personalInfo.funding_options || []).includes(option.id)
                        ? "border-[#739600]/50 bg-[#739600]/10"
                        : "border-white/[0.08] bg-black/30 hover:border-white/20"
                    }`}
                    onClick={() => {
                      const current = personalInfo.funding_options || [];
                      if (current.includes(option.id)) {
                        setPersonalInfo({ ...personalInfo, funding_options: current.filter(f => f !== option.id) });
                      } else {
                        setPersonalInfo({ ...personalInfo, funding_options: [...current, option.id] });
                      }
                    }}
                  >
                    <Checkbox
                      id={option.id}
                      checked={(personalInfo.funding_options || []).includes(option.id)}
                      onCheckedChange={(checked) => {
                        const current = personalInfo.funding_options || [];
                        if (checked) {
                          setPersonalInfo({ ...personalInfo, funding_options: [...current, option.id] });
                        } else {
                          setPersonalInfo({ ...personalInfo, funding_options: current.filter(f => f !== option.id) });
                        }
                      }}
                      className="border-[#739600] mt-0.5"
                    />
                    <div>
                      <Label htmlFor={option.id} className="text-white text-sm cursor-pointer font-medium">{option.label}</Label>
                      <p className="text-slate-500 text-xs mt-0.5">{option.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-8" data-testid="subsection-military">
            {/* Section Header */}
            <div className="relative">
              <div className="absolute -left-4 top-0 w-1 h-full bg-gradient-to-b from-[#4CAF50] to-[#81C784] rounded-full" />
              <h3 className="text-2xl font-light text-white tracking-tight">US Military Background</h3>
              <p className="text-slate-500 text-sm mt-1">Veteran status and military service information</p>
            </div>

            {/* Veteran Benefits - Glass Card */}
            <div className="backdrop-blur-xl bg-white/[0.02] border border-white/[0.05] rounded-2xl p-6 space-y-5 hover:border-[#4CAF50]/20 transition-colors duration-500">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-8 h-8 rounded-full bg-[#4CAF50]/10 flex items-center justify-center">
                  <Shield className="w-4 h-4 text-[#4CAF50]" />
                </div>
                <h4 className="text-xs font-medium uppercase tracking-wider text-slate-500">Military Service</h4>
              </div>
              
              <div className="space-y-3">
                <Label className="text-xs font-medium uppercase tracking-wider text-slate-400">
                  Have you ever served in the military? <span className="text-[#A1D8E0]">*</span>
                </Label>
                <RadioGroup
                  value={personalInfo.ever_served_military || ""}
                  onValueChange={(value) => setPersonalInfo({ ...personalInfo, ever_served_military: value })}
                  className="flex gap-4"
                >
                  {[{ value: "yes", label: "Yes" }, { value: "no", label: "No" }].map((option) => (
                    <div key={option.value} className={`flex items-center space-x-2 px-6 py-3 rounded-full border transition-all duration-300 cursor-pointer ${
                      personalInfo.ever_served_military === option.value 
                        ? "border-[#4CAF50]/50 bg-[#4CAF50]/10" 
                        : "border-white/[0.08] bg-black/30 hover:border-white/20"
                    }`}>
                      <RadioGroupItem value={option.value} id={`ever-served-${option.value}`} />
                      <Label htmlFor={`ever-served-${option.value}`} className="text-white cursor-pointer text-sm">{option.label}</Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>

              {personalInfo.ever_served_military === "yes" && (
                <>
                  <div className="space-y-2 pt-2 animate-in slide-in-from-top-2 duration-300">
                    <Label className="text-xs font-medium uppercase tracking-wider text-slate-400">Branch of Service</Label>
                    <Select
                      value={personalInfo.military_branch || ""}
                      onValueChange={(value) => setPersonalInfo({ ...personalInfo, military_branch: value })}
                    >
                      <SelectTrigger className="h-12 bg-black/30 border-white/[0.08] text-white rounded-full px-5 focus:border-[#A1D8E0] focus:ring-2 focus:ring-[#A1D8E0]/20 max-w-xs" data-testid="military-branch">
                        <SelectValue placeholder="Select branch" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#11161F] border-white/10 rounded-xl">
                        <SelectItem value="army">Army</SelectItem>
                        <SelectItem value="navy">Navy</SelectItem>
                        <SelectItem value="air_force">Air Force</SelectItem>
                        <SelectItem value="marines">Marines</SelectItem>
                        <SelectItem value="coast_guard">Coast Guard</SelectItem>
                        <SelectItem value="space_force">Space Force</SelectItem>
                        <SelectItem value="national_guard">National Guard</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="p-4 rounded-xl bg-[#4CAF50]/10 border border-[#4CAF50]/20 animate-in slide-in-from-top-2 duration-300">
                    <div className="flex items-center gap-2">
                      <svg className="w-5 h-5 text-[#4CAF50]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <p className="text-[#4CAF50] text-sm font-medium">
                        Thank you for your service!
                      </p>
                    </div>
                    <p className="text-slate-400 text-sm mt-2">
                      Our admissions team will contact you about military-friendly scheduling options and benefits.
                    </p>
                  </div>

                  <div className="space-y-3 pt-2 animate-in slide-in-from-top-2 duration-300">
                    <Label className="text-xs font-medium uppercase tracking-wider text-slate-400">
                      Will you be using veteran benefits to fund your education? <span className="text-[#A1D8E0]">*</span>
                    </Label>
                    <RadioGroup
                      value={personalInfo.veteran_benefits || ""}
                      onValueChange={(value) => setPersonalInfo({ ...personalInfo, veteran_benefits: value })}
                      className="flex gap-4"
                    >
                      {[{ value: "yes", label: "Yes, I qualify for VA benefits" }, { value: "no", label: "No" }].map((option) => (
                        <div key={option.value} className={`flex items-center space-x-2 px-5 py-3 rounded-full border transition-all duration-300 cursor-pointer ${
                          personalInfo.veteran_benefits === option.value 
                            ? "border-[#4CAF50]/50 bg-[#4CAF50]/10" 
                            : "border-white/[0.08] bg-black/30 hover:border-white/20"
                        }`}>
                          <RadioGroupItem value={option.value} id={`vet-benefits-${option.value}`} />
                          <Label htmlFor={`vet-benefits-${option.value}`} className="text-white cursor-pointer text-sm">{option.label}</Label>
                        </div>
                      ))}
                    </RadioGroup>
                  </div>

                  <div className="space-y-3 pt-2 animate-in slide-in-from-top-2 duration-300">
                    <Label className="text-xs font-medium uppercase tracking-wider text-slate-400">
                      Are you actively serving in the U.S. military? <span className="text-[#A1D8E0]">*</span>
                    </Label>
                    <RadioGroup
                      value={personalInfo.active_military || ""}
                      onValueChange={(value) => setPersonalInfo({ ...personalInfo, active_military: value })}
                      className="flex gap-4"
                    >
                      {[{ value: "yes", label: "Yes, currently serving" }, { value: "no", label: "No" }].map((option) => (
                        <div key={option.value} className={`flex items-center space-x-2 px-5 py-3 rounded-full border transition-all duration-300 cursor-pointer ${
                          personalInfo.active_military === option.value 
                            ? "border-[#4CAF50]/50 bg-[#4CAF50]/10" 
                            : "border-white/[0.08] bg-black/30 hover:border-white/20"
                        }`}>
                          <RadioGroupItem value={option.value} id={`active-mil-${option.value}`} />
                          <Label htmlFor={`active-mil-${option.value}`} className="text-white cursor-pointer text-sm">{option.label}</Label>
                        </div>
                      ))}
                    </RadioGroup>
                  </div>
                </>
              )}
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-8" data-testid="subsection-work">
            {/* Section Header */}
            <div className="relative">
              <div className="absolute -left-4 top-0 w-1 h-full bg-gradient-to-b from-[#FF9800] to-[#FFB74D] rounded-full" />
              <h3 className="text-2xl font-light text-white tracking-tight">Work Experience</h3>
              <p className="text-slate-500 text-sm mt-1">Your professional background and employment history</p>
            </div>

            {/* Experience Summary - Glass Card */}
            <div className="backdrop-blur-xl bg-white/[0.02] border border-white/[0.05] rounded-2xl p-6 space-y-5 hover:border-[#FF9800]/20 transition-colors duration-500">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-8 h-8 rounded-full bg-[#FF9800]/10 flex items-center justify-center">
                  <Briefcase className="w-4 h-4 text-[#FF9800]" />
                </div>
                <h4 className="text-xs font-medium uppercase tracking-wider text-slate-500">Experience Summary</h4>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs font-medium uppercase tracking-wider text-slate-400">
                    Years of Work Experience <span className="text-[#A1D8E0]">*</span>
                  </Label>
                  <Select
                    value={personalInfo.years_work_experience || ""}
                    onValueChange={(value) => setPersonalInfo({ ...personalInfo, years_work_experience: value })}
                  >
                    <SelectTrigger className="h-12 bg-black/30 border-white/[0.08] text-white rounded-full px-5 focus:border-[#A1D8E0] focus:ring-2 focus:ring-[#A1D8E0]/20" data-testid="work-years">
                      <SelectValue placeholder="Select experience level" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#11161F] border-white/10 rounded-xl">
                      <SelectItem value="0">No experience</SelectItem>
                      <SelectItem value="1-2">1-2 years</SelectItem>
                      <SelectItem value="3-5">3-5 years</SelectItem>
                      <SelectItem value="6-10">6-10 years</SelectItem>
                      <SelectItem value="10+">10+ years</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-medium uppercase tracking-wider text-slate-400">Current Job Title</Label>
                  <Input
                    value={personalInfo.current_job_title || ""}
                    onChange={(e) => setPersonalInfo({ ...personalInfo, current_job_title: e.target.value })}
                    placeholder="e.g., Registered Nurse, Physical Therapist"
                    className="h-12 bg-black/30 border-white/[0.08] focus:border-[#A1D8E0] focus:ring-2 focus:ring-[#A1D8E0]/20 text-white rounded-full px-5 transition-all duration-300 placeholder:text-slate-600"
                    data-testid="work-title"
                  />
                </div>
              </div>

              <div className="space-y-3">
                <Label className="text-xs font-medium uppercase tracking-wider text-slate-400">
                  Do you have OTA (Occupational Therapy Assistant) experience?
                </Label>
                <RadioGroup
                  value={personalInfo.ota_experience || ""}
                  onValueChange={(value) => setPersonalInfo({ ...personalInfo, ota_experience: value })}
                  className="flex gap-4"
                >
                  {[{ value: "yes", label: "Yes" }, { value: "no", label: "No" }].map((option) => (
                    <div key={option.value} className={`flex items-center space-x-2 px-6 py-3 rounded-full border transition-all duration-300 cursor-pointer ${
                      personalInfo.ota_experience === option.value 
                        ? "border-[#FF9800]/50 bg-[#FF9800]/10" 
                        : "border-white/[0.08] bg-black/30 hover:border-white/20"
                    }`}>
                      <RadioGroupItem value={option.value} id={`ota-${option.value}`} />
                      <Label htmlFor={`ota-${option.value}`} className="text-white cursor-pointer">{option.label}</Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>
            </div>

            {/* Employment History - Glass Card */}
            <div className="backdrop-blur-xl bg-white/[0.02] border border-white/[0.05] rounded-2xl p-6 space-y-5 hover:border-[#00677F]/20 transition-colors duration-500">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-[#00677F]/10 flex items-center justify-center">
                    <Users className="w-4 h-4 text-[#00677F]" />
                  </div>
                  <div>
                    <h4 className="text-xs font-medium uppercase tracking-wider text-slate-500">Employment History</h4>
                    <p className="text-slate-600 text-xs mt-0.5">Add your previous employers</p>
                  </div>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const employers = personalInfo.employers || [];
                    setPersonalInfo({
                      ...personalInfo,
                      employers: [...employers, { id: Date.now(), name: "", title: "", start_month: "", start_day: "", start_year: "", end_month: "", end_day: "", end_year: "", current: false, city: "", state: "" }]
                    });
                  }}
                  className="border-[#00677F]/50 text-[#00677F] hover:bg-[#00677F]/10 rounded-full px-4"
                  data-testid="add-employer-btn"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Employer
                </Button>
              </div>

              {(personalInfo.employers || []).length === 0 ? (
                <div className="text-center py-8 border border-dashed border-white/10 rounded-xl">
                  <Briefcase className="w-10 h-10 text-slate-600 mx-auto mb-3" />
                  <p className="text-slate-500 text-sm">No employers added yet</p>
                  <p className="text-slate-600 text-xs mt-1">Click "Add Employer" to start building your work history</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {(personalInfo.employers || []).map((employer, index) => (
                    <div key={employer.id} className="p-5 rounded-xl bg-black/20 border border-white/[0.05] space-y-4">
                      <div className="flex items-center justify-between">
                        <span className="text-white font-medium flex items-center gap-2">
                          <span className="w-6 h-6 rounded-full bg-[#00677F]/20 flex items-center justify-center text-xs text-[#00677F]">{index + 1}</span>
                          Employer {index + 1}
                        </span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            const employers = personalInfo.employers.filter(e => e.id !== employer.id);
                            setPersonalInfo({ ...personalInfo, employers });
                          }}
                          className="text-red-400 hover:text-red-300 hover:bg-red-400/10 rounded-full h-8 w-8 p-0"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Input
                          placeholder="Company / Employer Name"
                          value={employer.name}
                          onChange={(e) => {
                            const employers = personalInfo.employers.map(emp =>
                              emp.id === employer.id ? { ...emp, name: e.target.value } : emp
                            );
                            setPersonalInfo({ ...personalInfo, employers });
                          }}
                          className="h-11 bg-black/30 border-white/[0.08] text-white rounded-full px-5 focus:border-[#A1D8E0] focus:ring-2 focus:ring-[#A1D8E0]/20 placeholder:text-slate-600"
                        />
                        <Input
                          placeholder="Your Job Title"
                          value={employer.title}
                          onChange={(e) => {
                            const employers = personalInfo.employers.map(emp =>
                              emp.id === employer.id ? { ...emp, title: e.target.value } : emp
                            );
                            setPersonalInfo({ ...personalInfo, employers });
                          }}
                          className="h-11 bg-black/30 border-white/[0.08] text-white rounded-full px-5 focus:border-[#A1D8E0] focus:ring-2 focus:ring-[#A1D8E0]/20 placeholder:text-slate-600"
                        />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Input
                          placeholder="City"
                          value={employer.city || ""}
                          onChange={(e) => {
                            const employers = personalInfo.employers.map(emp =>
                              emp.id === employer.id ? { ...emp, city: e.target.value } : emp
                            );
                            setPersonalInfo({ ...personalInfo, employers });
                          }}
                          className="h-11 bg-black/30 border-white/[0.08] text-white rounded-full px-5 focus:border-[#A1D8E0] focus:ring-2 focus:ring-[#A1D8E0]/20 placeholder:text-slate-600"
                        />
                        <Input
                          placeholder="State"
                          value={employer.state || ""}
                          onChange={(e) => {
                            const employers = personalInfo.employers.map(emp =>
                              emp.id === employer.id ? { ...emp, state: e.target.value } : emp
                            );
                            setPersonalInfo({ ...personalInfo, employers });
                          }}
                          className="h-11 bg-black/30 border-white/[0.08] text-white rounded-full px-5 focus:border-[#A1D8E0] focus:ring-2 focus:ring-[#A1D8E0]/20 placeholder:text-slate-600"
                        />
                      </div>
                      {(() => {
                        const empMonths = [
                          { value: "1", label: "Jan" }, { value: "2", label: "Feb" }, { value: "3", label: "Mar" },
                          { value: "4", label: "Apr" }, { value: "5", label: "May" }, { value: "6", label: "Jun" },
                          { value: "7", label: "Jul" }, { value: "8", label: "Aug" }, { value: "9", label: "Sep" },
                          { value: "10", label: "Oct" }, { value: "11", label: "Nov" }, { value: "12", label: "Dec" }
                        ];
                        const empCurrentYear = new Date().getFullYear();
                        const empYears = Array.from({ length: 60 }, (_, i) => empCurrentYear - i);
                        const updateEmp = (field, value) => {
                          const employers = personalInfo.employers.map(emp =>
                            emp.id === employer.id ? { ...emp, [field]: value } : emp
                          );
                          setPersonalInfo({ ...personalInfo, employers });
                        };
                        const getDays = (m, y) => {
                          if (!m || !y) return 31;
                          return new Date(parseInt(y), parseInt(m), 0).getDate();
                        };
                        const startDays = Array.from({ length: getDays(employer.start_month, employer.start_year) }, (_, i) => i + 1);
                        const endDays = Array.from({ length: getDays(employer.end_month, employer.end_year) }, (_, i) => i + 1);
                        return (
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-1">
                              <Label className="text-xs text-slate-500">Start Date</Label>
                              <div className="grid grid-cols-3 gap-1.5">
                                <Select value={employer.start_month || ""} onValueChange={(v) => updateEmp("start_month", v)}>
                                  <SelectTrigger className="h-11 bg-black/30 border-white/[0.08] text-white rounded-full px-3 text-xs"><SelectValue placeholder="Month" /></SelectTrigger>
                                  <SelectContent className="bg-[#0f1419] border-white/10 text-white max-h-60">
                                    {empMonths.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                                  </SelectContent>
                                </Select>
                                <Select value={employer.start_day || ""} onValueChange={(v) => updateEmp("start_day", v)}>
                                  <SelectTrigger className="h-11 bg-black/30 border-white/[0.08] text-white rounded-full px-3 text-xs"><SelectValue placeholder="Day" /></SelectTrigger>
                                  <SelectContent className="bg-[#0f1419] border-white/10 text-white max-h-60">
                                    {startDays.map(d => <SelectItem key={d} value={String(d)}>{d}</SelectItem>)}
                                  </SelectContent>
                                </Select>
                                <Select value={employer.start_year || ""} onValueChange={(v) => updateEmp("start_year", v)}>
                                  <SelectTrigger className="h-11 bg-black/30 border-white/[0.08] text-white rounded-full px-3 text-xs"><SelectValue placeholder="Year" /></SelectTrigger>
                                  <SelectContent className="bg-[#0f1419] border-white/10 text-white max-h-60">
                                    {empYears.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs text-slate-500">End Date</Label>
                              <div className={`grid grid-cols-3 gap-1.5 ${employer.current ? "opacity-50 pointer-events-none" : ""}`}>
                                <Select value={employer.end_month || ""} onValueChange={(v) => updateEmp("end_month", v)} disabled={employer.current}>
                                  <SelectTrigger className="h-11 bg-black/30 border-white/[0.08] text-white rounded-full px-3 text-xs"><SelectValue placeholder="Month" /></SelectTrigger>
                                  <SelectContent className="bg-[#0f1419] border-white/10 text-white max-h-60">
                                    {empMonths.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                                  </SelectContent>
                                </Select>
                                <Select value={employer.end_day || ""} onValueChange={(v) => updateEmp("end_day", v)} disabled={employer.current}>
                                  <SelectTrigger className="h-11 bg-black/30 border-white/[0.08] text-white rounded-full px-3 text-xs"><SelectValue placeholder="Day" /></SelectTrigger>
                                  <SelectContent className="bg-[#0f1419] border-white/10 text-white max-h-60">
                                    {endDays.map(d => <SelectItem key={d} value={String(d)}>{d}</SelectItem>)}
                                  </SelectContent>
                                </Select>
                                <Select value={employer.end_year || ""} onValueChange={(v) => updateEmp("end_year", v)} disabled={employer.current}>
                                  <SelectTrigger className="h-11 bg-black/30 border-white/[0.08] text-white rounded-full px-3 text-xs"><SelectValue placeholder="Year" /></SelectTrigger>
                                  <SelectContent className="bg-[#0f1419] border-white/10 text-white max-h-60">
                                    {empYears.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>
                          </div>
                        );
                      })()}
                      <div className="flex items-center space-x-2 pt-1">
                        <Checkbox
                          id={`current-${employer.id}`}
                          checked={employer.current}
                          onCheckedChange={(checked) => {
                            const employers = personalInfo.employers.map(emp =>
                              emp.id === employer.id ? { ...emp, current: checked, end_month: checked ? "" : emp.end_month, end_day: checked ? "" : emp.end_day, end_year: checked ? "" : emp.end_year } : emp
                            );
                            setPersonalInfo({ ...personalInfo, employers });
                          }}
                          className="border-[#00677F]"
                        />
                        <Label htmlFor={`current-${employer.id}`} className="text-slate-400 text-sm cursor-pointer">I currently work here</Label>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        );

      case 6:
        return (
          <div className="space-y-8" data-testid="subsection-demographic">
            {/* Section Header */}
            <div className="relative">
              <div className="absolute -left-4 top-0 w-1 h-full bg-gradient-to-b from-[#9C27B0] to-[#CE93D8] rounded-full" />
              <h3 className="text-2xl font-light text-white tracking-tight">Demographic Information</h3>
              <p className="text-slate-500 text-sm mt-1">Optional information for statistical purposes</p>
            </div>

            {/* Notice */}
            <div className="p-4 rounded-xl bg-[#9C27B0]/5 border border-[#9C27B0]/10">
              <p className="text-slate-400 text-sm">
                We request this optional information to meet federal and state reporting requirements and to help the University of St. Augustine understand and serve its constituents. It will not be used as a basis for admission or in a discriminating manner. You will not be subject to adverse treatment if you do not provide this information. Please see our privacy statement for more information.
              </p>
            </div>

            {/* Ethnicity & Race - Glass Card */}
            <div className="backdrop-blur-xl bg-white/[0.02] border border-white/[0.05] rounded-2xl p-6 space-y-5 hover:border-[#9C27B0]/20 transition-colors duration-500">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-8 h-8 rounded-full bg-[#9C27B0]/10 flex items-center justify-center">
                  <Users className="w-4 h-4 text-[#9C27B0]" />
                </div>
                <h4 className="text-xs font-medium uppercase tracking-wider text-slate-500">Ethnicity & Race</h4>
              </div>
              
              <div className="space-y-2">
                <Label className="text-xs font-medium uppercase tracking-wider text-slate-400">Ethnicity</Label>
                <Select
                  value={personalInfo.ethnicity || ""}
                  onValueChange={(value) => setPersonalInfo({ ...personalInfo, ethnicity: value })}
                >
                  <SelectTrigger className="h-12 bg-black/30 border-white/[0.08] text-white rounded-full px-5 focus:border-[#A1D8E0] focus:ring-2 focus:ring-[#A1D8E0]/20 max-w-md" data-testid="demographic-ethnicity">
                    <SelectValue placeholder="Select ethnicity" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#11161F] border-white/10 rounded-xl">
                    <SelectItem value="hispanic">Hispanic or Latino</SelectItem>
                    <SelectItem value="not_hispanic">Not Hispanic or Latino</SelectItem>
                    <SelectItem value="prefer_not">Prefer not to say</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-3">
                <Label className="text-xs font-medium uppercase tracking-wider text-slate-400">Race (Select all that apply)</Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {[
                    { id: "american_indian", label: "American Indian or Alaska Native" },
                    { id: "asian", label: "Asian" },
                    { id: "black", label: "Black or African American" },
                    { id: "pacific_islander", label: "Native Hawaiian or Pacific Islander" },
                    { id: "white", label: "White" },
                    { id: "two_or_more", label: "Two or More Races" },
                    { id: "prefer_not_race", label: "Prefer not to say" },
                  ].map((option) => (
                    <div 
                      key={option.id} 
                      className={`flex items-center space-x-3 p-4 rounded-xl border transition-all duration-300 cursor-pointer ${
                        (personalInfo.race || []).includes(option.id)
                          ? "border-[#9C27B0]/50 bg-[#9C27B0]/10"
                          : "border-white/[0.08] bg-black/30 hover:border-white/20"
                      }`}
                      onClick={() => {
                        const current = personalInfo.race || [];
                        if (current.includes(option.id)) {
                          setPersonalInfo({ ...personalInfo, race: current.filter(r => r !== option.id) });
                        } else {
                          setPersonalInfo({ ...personalInfo, race: [...current, option.id] });
                        }
                      }}
                    >
                      <Checkbox
                        id={option.id}
                        checked={(personalInfo.race || []).includes(option.id)}
                        onCheckedChange={(checked) => {
                          const current = personalInfo.race || [];
                          if (checked) {
                            setPersonalInfo({ ...personalInfo, race: [...current, option.id] });
                          } else {
                            setPersonalInfo({ ...personalInfo, race: current.filter(r => r !== option.id) });
                          }
                        }}
                        className="border-[#9C27B0]"
                      />
                      <Label htmlFor={option.id} className="text-white text-sm cursor-pointer">{option.label}</Label>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* How did you hear about us - Glass Card */}
            <div className="backdrop-blur-xl bg-white/[0.02] border border-white/[0.05] rounded-2xl p-6 space-y-5 hover:border-[#00677F]/20 transition-colors duration-500">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-8 h-8 rounded-full bg-[#00677F]/10 flex items-center justify-center">
                  <svg className="w-4 h-4 text-[#00677F]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h4 className="text-xs font-medium uppercase tracking-wider text-slate-500">Referral Source</h4>
              </div>
              
              <div className="space-y-2">
                <Label className="text-xs font-medium uppercase tracking-wider text-slate-400">How did you hear about us?</Label>
                <p className="text-slate-600 text-xs">Help us understand how you found our program</p>
                <Select
                  value={personalInfo.referral_source || ""}
                  onValueChange={(value) => setPersonalInfo({ ...personalInfo, referral_source: value })}
                >
                  <SelectTrigger className="h-12 bg-black/30 border-white/[0.08] text-white rounded-full px-5 focus:border-[#A1D8E0] focus:ring-2 focus:ring-[#A1D8E0]/20 max-w-md" data-testid="demographic-referral">
                    <SelectValue placeholder="Select source" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#11161F] border-white/10 rounded-xl">
                    <SelectItem value="search_engine">Search Engine (Google, Bing)</SelectItem>
                    <SelectItem value="social_media">Social Media</SelectItem>
                    <SelectItem value="friend_family">Friend or Family</SelectItem>
                    <SelectItem value="employer">Employer</SelectItem>
                    <SelectItem value="college_fair">College Fair</SelectItem>
                    <SelectItem value="advertisement">Advertisement</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Section Complete Indicator */}
            <div className="flex items-center justify-center p-6 rounded-xl bg-gradient-to-r from-[#9C27B0]/10 to-[#00677F]/10 border border-white/[0.05]">
              <div className="text-center">
                <div className="w-12 h-12 rounded-full bg-[#739600]/20 flex items-center justify-center mx-auto mb-3">
                  <Check className="w-6 h-6 text-[#739600]" />
                </div>
                <p className="text-white font-medium">You're almost done!</p>
                <p className="text-slate-500 text-sm mt-1">Click "Section Complete" to finish Personal Information</p>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      {/* Sub-section Navigation */}
      <div className="lg:w-64 flex-shrink-0">
        <div className="glass-card rounded-xl p-2 space-y-1">
          {subSections.map((section) => (
            <button
              key={section.id}
              onClick={() => setActiveSubSection(section.id)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-all ${
                activeSubSection === section.id
                  ? "bg-[#00677F]/20 text-white border-l-2 border-[#A1D8E0]"
                  : "text-slate-400 hover:text-white hover:bg-white/5"
              }`}
              data-testid={`subsection-nav-${section.id}`}
            >
              <section.icon className={`w-4 h-4 ${
                activeSubSection === section.id 
                  ? "text-[#A1D8E0]" 
                  : isSubSectionComplete(section.id) 
                    ? "text-[#739600]" 
                    : ""
              }`} />
              <span className={`text-sm font-medium flex-1 ${
                isSubSectionComplete(section.id) && activeSubSection !== section.id
                  ? "text-[#739600]" 
                  : ""
              }`}>{section.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Sub-section Content */}
      <div className={`flex-1 ${isSubmitted ? 'pointer-events-none opacity-70' : ''}`}>
        {renderSubSection()}
        
        {/* Sub-section Navigation Buttons */}
        <div className="flex justify-between mt-8 pt-6 border-t border-white/10">
          <Button
            onClick={goPrev}
            disabled={isFirst}
            variant="outline"
            className={`rounded-full px-6 ${theme === 'dark' ? 'border-white/20 text-white hover:bg-white/5' : 'border-gray-300 text-gray-700 hover:bg-gray-100'}`}
            data-testid="prev-subsection-btn"
          >
            Previous Section
          </Button>
          
          {!isLast ? (
            <Button
              onClick={goNext}
              className="bg-[#00677F] hover:bg-[#135163] text-white rounded-full px-6"
              data-testid="next-subsection-btn"
            >
              Next Section
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={() => onSectionComplete && onSectionComplete()}
              className="bg-[#739600] hover:bg-[#218838] text-white rounded-full px-6"
              data-testid="complete-personal-btn"
            >
              <Check className="w-4 h-4 mr-2" />
              Section Complete
            </Button>
          )}
        </div>
      </div>

      {/* SSN Reminder Modal */}
      {showSsnReminder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="ssn-reminder-modal">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
          <div className="relative w-full max-w-lg rounded-2xl overflow-hidden shadow-2xl shadow-black/50 animate-in fade-in zoom-in-95 duration-200">
            <div className="px-6 py-5 bg-gradient-to-r from-[#00677F] to-[#005a6b]">
              <h4 className="text-white text-lg font-semibold font-['Montserrat']">Social Security Number Recommended</h4>
            </div>
            <div className="bg-[#0f1419] px-6 py-6">
              <p className="text-slate-300 text-sm leading-relaxed">
                You indicated that you may need federal loans to help pay for school. To process federal financial aid, a valid Social Security Number (SSN) is required. You don't have to provide your SSN now, but we strongly recommend adding it so there's no delay with your financial aid later.
              </p>
            </div>
            <div className="bg-[#0f1419] border-t border-white/[0.06] px-6 pb-6 flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setShowSsnReminder(false);
                }}
                className="border-white/20 text-slate-300 hover:bg-white/5 rounded-xl px-5"
                data-testid="ssn-reminder-go-back"
              >
                Go Back
              </Button>
              <Button
                onClick={() => {
                  setShowSsnReminder(false);
                  setActiveSubSection(visibleIds[currentIndex + 1]);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                className="bg-[#00677F] hover:bg-[#005a6b] text-white rounded-xl px-5"
                data-testid="ssn-reminder-continue"
              >
                Acknowledge & Continue
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Age Verification Modal - Rendered at component level so it appears regardless of active sub-section */}
      {showAgeVerifyModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="age-verify-modal">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
          <div className="relative w-full max-w-md rounded-2xl overflow-hidden shadow-2xl shadow-black/50 animate-in fade-in zoom-in-95 duration-200">
            <div className="px-6 py-5 bg-gradient-to-r from-[#FF9800] to-[#FF6B35]">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h4 className="text-white text-lg font-semibold">Age Verification</h4>
                  <p className="text-white/70 text-xs">Applicant Eligibility Check</p>
                </div>
              </div>
            </div>
            <div className="bg-[#0f1419] px-6 py-5 space-y-4">
              <p className="text-slate-300 text-sm leading-relaxed">
                The date of birth entered indicates the applicant is <strong className="text-white">under 10 years old</strong>. 
                Applicants must be at least 10 years of age to proceed with an application.
              </p>
              <p className="text-slate-400 text-sm">
                Are you sure this date of birth is correct?
              </p>
            </div>
            <div className="bg-[#0f1419] border-t border-white/[0.06] px-6 pb-6 flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setShowAgeVerifyModal(false);
                  setPendingDob("");
                }}
                className="border-white/20 text-slate-300 hover:bg-white/5 rounded-xl px-5"
                data-testid="age-verify-correct-btn"
              >
                No, Let Me Fix It
              </Button>
              <Button
                onClick={() => {
                  setPersonalInfo({ ...personalInfo, date_of_birth: pendingDob, age_restricted: true });
                  setShowAgeVerifyModal(false);
                  setPendingDob("");
                  if (onAgeRestriction) onAgeRestriction();
                }}
                className="bg-[#DC5034] hover:bg-[#c04530] text-white rounded-xl px-5"
                data-testid="age-verify-confirm-btn"
              >
                Yes, This Is Correct
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PersonalInfoSections;
